import { AspectRatio } from '@radix-ui/react-aspect-ratio';
import { CrossMarketRoute } from '../types/cross-market-route';
import { getDynamicCurrency } from '@/util-conversion/currency';
import { memo, MouseEventHandler } from 'react';
import MissingImagePlaceholder from './missing-image-placeholder';
import PickupAvailability from './pickup-availability';
import ShippingDetails from './cross-market/shipping-details';
import Spacer from '@/common/components/spacer';
import VehicleSubDetails from './vehicle/vehicle-sub-details';
import VehicleTileContainer from './vehicle-tile-container';
import VehicleBadges from './vehicle-badges';
import { cn } from '@/shadcn-ui/utils';
import JustMissedPill from '@/homepage/components/live-large/just-missed-pill';
import { useImageLoader } from '@/catalog/hooks/use-image-loader';
import { motion } from 'motion/react';
import { ShippingFeeBreakdown } from '../types/shipping-fee-breakdown';
import shouldShowShippingFee from '../utils/should-show-shipping-fee';

export interface VehicleTileProps {
  image?: string;
  modelYear: number;
  make: string;
  model: string;
  trim: string;
  color: string;
  price?: number;
  odometer: number;
  earliestAvailableDate: string;
  isRecentlyAdded: boolean;
  isOrderable: boolean;
  isNewVehicle: boolean;
  isElectric: boolean;
  pickupDays?: number;
  totalSavingsPerYear?: number;
  crossMarketRoute?: CrossMarketRoute;
  shippingFeeBreakdown?: ShippingFeeBreakdown;
  savingsCalculatorAvailable?: boolean;
  /** Delegate that fetches savings calculator data for the vehicle */
  fetchSavingsCalculator?: MouseEventHandler<HTMLDivElement>;
  /** Unique ID for the savings pill. This is used to assign a trigger for the coachmark. */
  savingsV2PillId: string;
  /** If true, a coach mark popover will be shown on the savings pill */
  showCoachMark?: boolean;
  /** Indicates if image loading into DOM should take priority */
  imageLoadPriority?: 'high' | 'low';
  /** If true, the image will fade in */
  fadeInImage?: boolean;
}

const VehicleTile = memo(function VehicleTile({
  image,
  modelYear,
  make,
  model,
  trim,
  color,
  price,
  odometer,
  earliestAvailableDate,
  isRecentlyAdded,
  isOrderable,
  isNewVehicle,
  isElectric,
  pickupDays,
  totalSavingsPerYear,
  crossMarketRoute,
  shippingFeeBreakdown,
  savingsCalculatorAvailable = false,
  fetchSavingsCalculator,
  savingsV2PillId,
  showCoachMark,
  imageLoadPriority = 'low',
  fadeInImage = false,
}: VehicleTileProps) {
  const isHighPriority = imageLoadPriority === 'high';

  return (
    <VehicleTileContainer>
      <VehicleBadges
        isNewVehicle={isNewVehicle}
        isElectric={isElectric}
        isRecentlyAdded={isRecentlyAdded}
        savingsCalculatorAvailable={savingsCalculatorAvailable}
        totalSavingsPerYear={totalSavingsPerYear}
        savingsV2PillId={savingsV2PillId}
        showCoachMark={showCoachMark}
        fetchSavingsCalculator={fetchSavingsCalculator}
      />

      <div className="relative h-full w-full">
        <div className={cn({ 'opacity-40': !isOrderable })}>
          {!image ? (
            <AspectRatio ratio={4 / 3} className="h-full w-full px-2 pt-2">
              <MissingImagePlaceholder />
            </AspectRatio>
          ) : (
            <AspectRatio ratio={4 / 3}>
              {fadeInImage ? (
                <FadeInImage
                  image={image}
                  modelYear={modelYear}
                  make={make}
                  model={model}
                  isHighPriority={isHighPriority}
                />
              ) : (
                <img
                  src={image}
                  alt={`${modelYear} ${make} ${model}`}
                  fetchPriority={isHighPriority ? 'high' : 'auto'}
                  loading={isHighPriority ? 'eager' : 'lazy'}
                  className="h-full w-full"
                />
              )}
            </AspectRatio>
          )}
        </div>

        {!isOrderable ? (
          <JustMissedPill className="left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
        ) : null}
      </div>

      <Spacer variant="height24" />

      <div className="px-[24px]">
        <p className="body-strong">
          {modelYear}・{make} {model}
        </p>

        <VehicleSubDetails
          trim={trim}
          odometer={odometer}
          color={color}
          isNewVehicle={isNewVehicle}
        />

        <Spacer variant="height8" />

        {price ? <h3>{getDynamicCurrency(price)}/mo</h3> : null}
      </div>

      <Spacer variant="height16" />

      <div className="h-px w-full bg-flexcar-neutral80" />

      <div className="px-[24px]">
        <div className="flex flex-row justify-between">
          {isOrderable ? (
            <>
              {/* Due to shipping details being a clickable element that gets extra padding to the clickable dom element itself, we add padding here as well to match */}
              <div className="py-[16px]">
                <PickupAvailability
                  pickupDays={pickupDays}
                  earliestAvailableDate={earliestAvailableDate}
                />
              </div>

              {shouldShowShippingFee(crossMarketRoute, shippingFeeBreakdown) ? (
                <ShippingDetails
                  crossMarketRoute={crossMarketRoute}
                  shippingFeeBreakdown={shippingFeeBreakdown}
                  contentAlign="end"
                  variant="body3"
                />
              ) : null}
            </>
          ) : (
            <p className="body3 py-[16px] text-flexcar-neutral1">No longer available</p>
          )}
        </div>
      </div>
    </VehicleTileContainer>
  );
});

function FadeInImage({
  image,
  modelYear,
  make,
  model,
  isHighPriority,
}: {
  image: string;
  modelYear: number;
  make: string;
  model: string;
  isHighPriority: boolean;
}) {
  const { motionProps, imgProps, placeholderAnimate } = useImageLoader({
    src: image,
    isHighPriority,
  });

  return (
    <>
      {/* Gradient placeholder */}
      <motion.div
        className="absolute inset-0 bg-[radial-gradient(ellipse_150%_100%_at_center_95%,#EDEEEF_0%,#EDEEEF_20%,#EDEEEF_35%,#EDEEEF_50%,#F3F5F7_70%)]"
        initial={{ opacity: 1 }}
        animate={placeholderAnimate}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
      />

      <motion.img
        alt={`${modelYear} ${make} ${model}`}
        className="h-full w-full object-cover"
        sizes="(max-width: 639px) 100vw, (max-width: 1114px) 50vw, (max-width: 1364px) 33vw, 25vw"
        // Motion props
        initial={motionProps.initial}
        animate={motionProps.animate}
        transition={motionProps.transition}
        // Image props
        src={imgProps.src}
        loading={imgProps.loading}
        fetchPriority={imgProps.fetchPriority}
        onLoad={imgProps.onLoad}
        onError={imgProps.onError}
      />
    </>
  );
}

VehicleTile.displayName = 'VehicleTile';

export default VehicleTile;
